'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useSyncExternalStore } from 'react'

/** The name a page gave itself, tied to the path it was given on. */
type PageTitle = { pathname: string; title: string }

let current: PageTitle | null = null
const subscribers = new Set<() => void>()

function publish(next: PageTitle | null): void {
  current = next
  for (const notify of subscribers) {
    notify()
  }
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify)
  return (): void => {
    subscribers.delete(notify)
  }
}

function read(): PageTitle | null {
  return current
}

/**
 * Names the page in the browser tab, e.g. the account or chat it shows. A blank
 * or missing name leaves the route's section name.
 */
export function usePageTitle(title: string | null | undefined): void {
  const pathname = usePathname()
  const trimmed = title?.trim() ?? ''
  useEffect(() => {
    if (trimmed === '') {
      return
    }
    const entry: PageTitle = { pathname, title: trimmed }
    publish(entry)
    return (): void => {
      if (current === entry) {
        publish(null)
      }
    }
  }, [pathname, trimmed])
}

/**
 * The name the page on `pathname` gave itself. A name set on another path is
 * ignored, so a stale one never outlives the navigation away from its page.
 */
export function usePageTitleFor(pathname: string): string | null {
  const entry = useSyncExternalStore(subscribe, read, () => null)
  return entry !== null && entry.pathname === pathname ? entry.title : null
}
